<template>
  <div class="chat-container">
    <!-- Floating Button -->
    <v-btn
      v-if="!chatOpen"
      fab
      dark
      :style="headerStyle"
      class="floating-chat-button"
      elevation="8"
      @click="openChat"
    >
      <v-avatar size="40">
        <img v-if="useImageLogo" :src="chatLogoSrc" />
        <v-icon large v-else>{{ floatingIconName }}</v-icon>
      </v-avatar>
    </v-btn>

    <!-- Chat Panel -->
    <v-slide-x-reverse-transition>
      <v-card v-if="chatOpen" class="chat-panel rounded-xl elevation-8">
        <!-- Chat Header -->
        <v-card-title class="chat-header pa-3" :style="headerStyle">
          <v-avatar size="36" class="mr-2">
            <img v-if="useImageLogo" :src="chatLogoSrc" alt="logo" />
            <v-icon v-else color="white">{{ floatingIconName }}</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="text-subtitle-1 font-weight-bold">
              {{
                aiChatConfig?.assistant?.name ||
                $t("assistantChatViewer.assistant_name")
              }}
            </div>
            <v-chip x-small color="success" dark class="mt-n1">
              <v-icon x-small left>mdi-circle</v-icon>
              {{ $t("assistantChatViewer.online") }}
            </v-chip>
          </div>
          <v-spacer />

          <v-btn
            icon
            small
            @click="newChat"
            color="white"
            :title="$t('assistantChatViewer.new_chat')"
          >
            <v-icon small>mdi-message-plus</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            @click="clearChat"
            color="white"
            :title="$t('assistantChatViewer.clear_chat')"
          >
            <v-icon small>mdi-trash-can</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            @click="chatOpen = false"
            color="white"
            :title="$t('assistantChatViewer.close')"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <!-- Chat Messages -->
        <v-card-text
          ref="chatContainer"
          class="chat-messages pa-3"
          :style="chatMessagesStyle"
        >
          <div v-if="messages.length === 0" class="text-center py-6">
            <v-avatar size="50">
              <v-icon v-if="useImageLogo" size="48" color="grey lighten-1"
                >mdi-robot</v-icon
              >
              <v-icon v-else size="48" color="grey lighten-1">{{
                floatingIconName
              }}</v-icon>
            </v-avatar>
          </div>

          <div
            v-for="(message, index) in messages"
            :key="index"
            :class="[
              'message-wrapper mb-3 d-flex',
              message.role !== 'user' ? 'justify-end' : 'justify-start',
            ]"
          >
            <div
              :class="[
                'message-bubble pa-3 rounded-lg',
                message.role === 'user' ? 'user-message' : 'assistant-message',
              ]"
              :style="
                message.role === 'user'
                  ? userMessageStyle
                  : assistantMessageStyle
              "
              style="max-width: 85%"
            >
              <div
                v-if="message.role === 'assistant'"
                class="d-flex align-center mb-1"
              >
                <v-avatar size="24" class="ma-1">
                  <img v-if="useImageLogo" :src="chatLogoSrc" alt="logo" />
                  <v-icon v-else :style="headerStyle">{{
                    floatingIconName
                  }}</v-icon>
                </v-avatar>
                <span class="ml-1 text-caption font-weight-bold">{{
                  $t("assistantChatViewer.assistant")
                }}</span>
              </div>
              <div
                class="message-content text-body-2"
                v-html="
                  formatMessage(
                    index === messages.length - 1 &&
                      message.role === 'assistant' &&
                      isTyping
                      ? typingText
                      : message.content,
                  )
                "
              ></div>
              <span
                v-if="
                  index === messages.length - 1 &&
                  message.role === 'assistant' &&
                  isTyping
                "
                class="typing-cursor"
                >|</span
              >

              <div v-if="message.showUpgradeButton && !isTyping" class="mt-2">
                <v-btn
                  x-small
                  outlined
                  color="primary"
                  @click="openUpgradeDialog"
                >
                  <v-icon left x-small>mdi-autorenew</v-icon>
                  {{ $t("assistantChatViewer.renew") }}
                </v-btn>
              </div>

              <div
                class="message-time text-caption mt-1 grey--text text--lighten-1"
              >
                {{ formatTime(message.timestamp) }}
              </div>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="d-flex align-center justify-start mb-3">
            <v-progress-circular
              indeterminate
              size="20"
              width="2"
              color="primary"
              class="mr-2"
            />
            <span class="text-caption grey--text">{{
              $t("assistantChatViewer.processing")
            }}</span>
          </div>
        </v-card-text>

        <v-divider />

        <!-- Chat Input -->
        <v-card-actions class="chat-input pa-2">
          <v-btn
            :disabled="!userMessage.trim() || loading"
            :loading="loading"
            :style="headerStyle"
            fab
            small
            dark
            class="ml-2"
            @click="sendMessage"
          >
            <v-icon small>mdi-send</v-icon>
          </v-btn>
          <v-textarea
            v-model="userMessage"
            :placeholder="$t('assistantChatViewer.message_placeholder')"
            rows="1"
            auto-grow
            outlined
            dense
            hide-details
            class="rounded-lg"
            @keydown.enter.exact.prevent="sendMessage"
          />
        </v-card-actions>
      </v-card>
    </v-slide-x-reverse-transition>
  </div>
</template>

<script>
export default {
  name: "AssistantChatViewer",

  props: {
    aiChatConfig: Object,
  },

  computed: {
    pulseColor() {
      const settings = this.chatSettings;
      let color = settings.headerColor || "#667eea";

      if (settings.headerGradient && settings.headerColor2) {
      }
      return color;
    },

    pulseStyle() {
      return {
        "--pulse-color": this.pulseColor,
      };
    },
    chatSettings() {
      return this.aiChatConfig?.chatSettings || {};
    },

    headerStyle() {
      const settings = this.chatSettings;
      let background = settings.headerColor || "#667eea";

      if (settings.headerGradient && settings.headerColor2) {
        background = `linear-gradient(135deg, ${settings.headerColor} 0%, ${settings.headerColor2} 100%)`;
      }

      return {
        background,
        color: "white",
      };
    },

    chatMessagesStyle() {
      const settings = this.chatSettings;

      if (settings.useImageBackground && settings.backgroundImage) {
        return {
          backgroundImage: `url(${settings.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        };
      }

      return {
        background: settings.backgroundColor || "#fafafa",
      };
    },

    userMessageStyle() {
      const settings = this.chatSettings;
      let background = settings.headerColor || "#667eea";

      if (settings.headerGradient && settings.headerColor2) {
        background = `linear-gradient(135deg, ${settings.headerColor} 0%, ${settings.headerColor2} 100%)`;
      }

      return {
        background,
        color: "white",
      };
    },

    assistantMessageStyle() {
      const settings = this.chatSettings;
      return {
        color: settings.textColor || "#333333",
        fontSize: `${settings.fontSize || 14}px`,
      };
    },

    floatingIconName() {
      return this.chatSettings.floatingIcon || "mdi-robot";
    },

    chatLogoSrc() {
      return this.chatSettings.chatLogo;
    },

    useImageLogo() {
      return this.chatSettings.useImageLogo && this.chatLogoSrc;
    },
  },

  data() {
    return {
      typingText: "",
      isTyping: false,
      typingTimeout: null,
      displayedText: "",
      index: 0,
      typingSpeed: 50,
      chatOpen: false,
      messages: [],
      userMessage: "",
      loading: false,
      assistantName: "",
      promptText: "",
      previse_messages: [],
      showUpgradeButton: false,
      showUpgradeDialog: false,
      currentPackage: null,
    };
  },

  beforeDestroy() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  },
  methods: {
    closeChatPermanently() {
      this.chatOpen = false;
    },
    typeText(fullText, messageIndex) {
      if (!fullText || fullText.trim() === "") {
        this.isTyping = false;
        return;
      }
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }
      this.isTyping = true;
      this.typingText = "";
      const totalDuration = fullText.length * this.typingSpeed;
      const charCount = fullText.length;
      const delayPerChar = totalDuration / charCount;
      const adjustedDelay = Math.min(Math.max(delayPerChar, 10), 25);
      let currentIndex = 0;

      const typeNextChar = () => {
        if (currentIndex < charCount) {
          this.typingText = fullText.substring(0, currentIndex + 1);
          currentIndex++;
          this.typingTimeout = setTimeout(typeNextChar, adjustedDelay);

          this.$nextTick(() => {
            this.scrollToBottom();
          });
        } else {
          this.isTyping = false;
          this.typingText = fullText;
          this.typingTimeout = null;
        }
      };
      typeNextChar();
    },
    typeWriter() {
      if (this.index < this.fullText.length) {
        this.displayedText += this.fullText.charAt(this.index);
        this.index++;
        setTimeout(this.typeWriter, this.typingSpeed);
      }
    },
    openChat() {
      this.chatOpen = true;
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    async sendMessage() {
      if (!this.userMessage.trim() || this.loading) return;

      const userMsg = this.userMessage.trim();

      this.userMessage = "";

      this.messages.push({
        role: "user",
        content: userMsg,
        timestamp: new Date(),
      });

      this.loading = true;
      this.scrollToBottom();
      this.showUpgradeButton = false;

      try {
        // const { data } = await this.$api.post(this.$aiApi + "/assistant/test", {
        //   message: userMsg,
        //   assist_id: this.assistantId,
        //   previse_messages: this.previse_messages,
        // });
        const { data } = {};
        let response =
          data?.data?.output[0]?.content[0]?.text ||
          data.output[0].content[0].text ||
          data?.text ||
          data?.choices?.[0]?.message?.content ||
          "";
        this.messages.push({
          role: "assistant",
          content: response || $t("assistantChatViewer.error_message"),
          timestamp: new Date(),
        });
        this.$nextTick(() => {
          this.typeText(
            response || $t("assistantChatViewer.error_message"),
            this.messages.length - 1,
          );
          this.scrollToBottom();
        });
        this.previse_messages.push(
          {
            role: "user",
            content: userMsg,
          },
          {
            role: "assistant",
            content: response || $t("assistantChatViewer.error_message"),
          },
        );
      } catch ({ response }) {
        let messageError = $t("assistantChatViewer.preview_unavailable");
        if (response?.status === 400) {
          messageError = response.data.message;
          this.showUpgradeButton = true;
        }
        const errorMessage = {
          role: "assistant",
          content: messageError,
          timestamp: new Date(),
          showUpgradeButton: this.showUpgradeButton,
        };

        this.messages.push(errorMessage);
        this.$nextTick(() => {
          this.typeText(messageError, this.messages.length - 1);
        });
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    openUpgradeDialog() {
      this.currentPackage = {
        id: this.assistantId,
        name: this.assistantName || $t("assistantChatViewer.assistant_name"),
      };
      this.showUpgradeDialog = true;
    },

    newChat() {
      this.$modal
        .yesNo($t("assistantChatViewer.confirm_new_chat"))
        .then((value) => {
          if (value === true) {
            this.clearChat();
          }
        });
    },

    clearChat() {
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = null;
      }
      this.messages = [];
      this.userMessage = "";
      this.typingText = "";
      this.previse_messages = [];
      this.isTyping = false;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatContainer) {
          this.$refs.chatContainer.scrollTop =
            this.$refs.chatContainer.scrollHeight;
        }
      });
    },

    formatMessage(content) {
      return content.replace(/\n/g, "<br>");
    },

    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString("fa-IR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped>
.chat-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 14;
}

.floating-chat-button {
  position: absolute !important;
  z-index: 15;
  animation: pulse 2s infinite;
  bottom: 55px !important;
  right: 12px !important;
  left: auto !important;
  width: 44px !important;
  height: 44px !important;
  pointer-events: auto;
}

.floating-chat-button:hover {
  transform: scale(1.1);
}

.chat-panel {
  position: absolute !important;
  bottom: 10px;
  left: 10px;
  right: 10px;
  max-height: calc(100% - 20px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
  z-index: 15;
}

.chat-header {
  color: white;
  flex-shrink: 0;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  max-height: 280px;
  min-height: 150px;
}

.chat-input {
  background: #fff;
  flex-shrink: 0;
}

.user-message {
  color: white;
  border-radius: 16px 16px 4px 16px !important;
  position: relative;
}

.assistant-message {
  background: #fff;
  border-radius: 16px 16px 16px 4px !important;
  border: 1px solid #e0e0e0;
  position: relative;
}

.message-content {
  word-wrap: break-word;
  white-space: pre-wrap;
}

.typing-cursor {
  display: inline-block;
  animation: blink 1s infinite;
  font-weight: bold;
  color: #333;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(102, 126, 234, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
  }
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}
</style>
